import { PUB_SUB_EVENTS, ON_CHANGE_DEBOUNCE_TIMER } from '../base/constants';
import { debounce, fetchConfig, trapFocus } from '../base/global';
import { subscribe, publish } from '../base/pubsub';

/**
 * Updates the header cart count bubble.
 * Used when cart-drawer-items is not in the DOM (empty cart), so pubsub cannot sync the count.
 * @param {number} itemCount - The current number of items in the cart.
 */
export function updateCartCountBubble(itemCount) {
  const cartCountBubbleItems = document.querySelector('.cart-count-bubble .visually-hidden');
  const cartCountBubbleItemsCounter = document.querySelector('.cart-count-bubble [aria-hidden="true"]');
  const count = itemCount || 0;

  if (cartCountBubbleItems) {
    cartCountBubbleItems.textContent = count === 1 ? '1 item' : `${count} items`;
  }

  if (cartCountBubbleItemsCounter) {
    cartCountBubbleItemsCounter.textContent = `${count}`;
  }
}

/**
 * Custom element for cart remove button functionality.
 * Handles removing items from the cart when clicked.
 */
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && typeof (/** @type {any} */ (cartItems).updateQuantity) === 'function') {
        /** @type {any} */ (cartItems).updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

/**
 * Custom element for managing cart items functionality.
 * Handles cart updates, quantity changes, and cart state management.
 */
export class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = /** @type {(() => void) | null} */ (null);

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source }) => {
      if (source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });

    // Initialise cart page lightbox if present.
    this.initCartPageLightbox();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber && typeof this.cartUpdateUnsubscriber === 'function') {
      this.cartUpdateUnsubscriber();
    }
  }

  /**
   * Resets the quantity input to its original value.
   * @param {string} id - The ID of the quantity input to reset.
   */
  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input && input instanceof HTMLInputElement) {
      input.value = input.getAttribute('value') || '';
      this.isEnterPressed = false;
    }
  }

  /**
   * Sets validation message for quantity input.
   * @param {Event} event - The input event.
   * @param {string} index - The item index.
   * @param {string} message - The validation message.
   */
  setValidity(event, index, message) {
    if (event.target instanceof HTMLInputElement) {
      event.target.setCustomValidity(message);
      event.target.reportValidity();
      this.resetQuantityInput(index);
      event.target.select();
    }
  }

  /**
   * Validates quantity input and updates cart if valid.
   * @param {Event} event - The input event.
   */
  validateQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const inputValue = parseInt(event.target.value, 10);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < parseInt(event.target.dataset.min || '0', 10)) {
      const minError = /** @type {any} */ (window).quickOrderListStrings?.min_error;
      message = minError ? minError.replace('[min]', event.target.dataset.min || '') : 'Minimum quantity not met';
    } else if (inputValue > parseInt(event.target.max || '999999', 10)) {
      const maxError = /** @type {any} */ (window).quickOrderListStrings?.max_error;
      message = maxError ? maxError.replace('[max]', event.target.max || '') : 'Maximum quantity exceeded';
    } else if (inputValue % parseInt(event.target.step || '1', 10) !== 0) {
      const stepError = /** @type {any} */ (window).quickOrderListStrings?.step_error;
      message = stepError ? stepError.replace('[step]', event.target.step || '') : 'Invalid quantity step';
    }

    if (message) {
      this.setValidity(event, index || '', message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      const activeElement = document.activeElement;
      const name = activeElement instanceof HTMLElement ? activeElement.getAttribute('name') || '' : '';
      this.updateQuantity(index || '', inputValue, name, event.target.dataset.quantityVariantId || '');
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      // Store the current horizontal slider instance before DOM replacement.
      const currentHorizontalSlider = document.querySelector('.js-cart-drawer-collection-slider-horizontal');
      let preservedSliderInstance = null;
      let preservedSliderConfig = null;

      if (currentHorizontalSlider && /** @type {any} */ (currentHorizontalSlider).swiper) {
        preservedSliderInstance = /** @type {any} */ (currentHorizontalSlider).swiper;
        // Store the current configuration and state.
        preservedSliderConfig = {
          activeIndex: preservedSliderInstance.activeIndex,
          slidesPerView: 2.1, // Use fixed value instead of preserving auto.
          spaceBetween: preservedSliderInstance.params.spaceBetween,
          width: preservedSliderInstance.width,
          slides: Array.from(preservedSliderInstance.slides).map((slide) => slide.outerHTML)
        };
      }

      const cartUrl = /** @type {any} */ (window).routes?.cart_url || '/cart';
      fetch(`${cartUrl}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // Update cart-drawer-items (cart items list).
          const targetCartItems = document.querySelector('cart-drawer-items');
          const sourceCartItems = html.querySelector('cart-drawer-items');
          if (targetCartItems && sourceCartItems) {
            targetCartItems.replaceWith(sourceCartItems);
          }

          // Update cart drawer footer (threshold, totals, etc.).
          const targetFooter = document.querySelector('.js-cart-drawer-footer');
          const sourceFooter = html.querySelector('.js-cart-drawer-footer');
          if (targetFooter && sourceFooter) {
            targetFooter.replaceWith(sourceFooter);
          }

          // Fetch current cart data to get accurate count.
          fetch('/cart.js')
            .then((cartResponse) => {
              if (cartResponse.ok) {
                return cartResponse.json();
              }
              throw new Error('Failed to fetch cart data');
            })
            .then((cartData) => {
              updateCartCountBubble(cartData.item_count || 0);

              // Also update checkout button total from server-rendered footer.
              const checkoutButton = document.querySelector('#CartDrawer-Checkout');
              if (checkoutButton) {
                const sourceCheckoutButton = html.querySelector('#CartDrawer-Checkout');
                if (sourceCheckoutButton) {
                  const sourceTotalSpans = sourceCheckoutButton.querySelectorAll('span');
                  const targetTotalSpans = checkoutButton.querySelectorAll('span');
                  if (sourceTotalSpans.length >= 3 && targetTotalSpans.length >= 3) {
                    targetTotalSpans[2].textContent = sourceTotalSpans[2].textContent;
                  }
                }
              }
            })
            .catch((error) => {
              console.error('Error updating cart count:', error);
            });

          // Update cart drawer empty state classes.
          const cartDrawer = document.querySelector('cart-drawer');
          const sourceCartDrawer = html.querySelector('cart-drawer');
          if (cartDrawer && sourceCartDrawer) {
            // Update empty state classes.
            cartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
          }

          // After replacing DOM sections, reorder gift wrap rows in the drawer.
          try {
            // @ts-expect-error - CartDrawer is attached to window by cart-drawer component.
            if (window.CartDrawer && typeof window.CartDrawer.reorderGiftWrapRows === 'function') {
              // @ts-expect-error - CartDrawer is attached to window by cart-drawer component.
              window.CartDrawer.reorderGiftWrapRows();
            }
          } catch (err) {
            console.error('Error invoking CartDrawer.reorderGiftWrapRows:', err);
          }

          // Restore horizontal slider state and prevent reinitialization.
          setTimeout(() => {
            const newHorizontalSlider = document.querySelector('.js-cart-drawer-collection-slider-horizontal');
            if (newHorizontalSlider && preservedSliderConfig) {
              // Prevent automatic initialization by setting init="false".
              newHorizontalSlider.setAttribute('init', 'false');

              // Wait for any automatic initialization to complete, then restore state.
              setTimeout(() => {
                // If the slider was automatically initialized, destroy it first.
                if (/** @type {any} */ (newHorizontalSlider).swiper) {
                  /** @type {any} */ (newHorizontalSlider).swiper.destroy(true, true);
                }

                // Apply the preserved configuration.
                Object.assign(newHorizontalSlider, {
                  slidesPerView: preservedSliderConfig.slidesPerView,
                  spaceBetween: preservedSliderConfig.spaceBetween
                });

                // Initialize with preserved configuration.
                /** @type {any} */ (newHorizontalSlider).initialize();

                // After initialization, restore the active slide if possible.
                setTimeout(() => {
                  if (/** @type {any} */ (newHorizontalSlider).swiper) {
                    const swiper = /** @type {any} */ (newHorizontalSlider).swiper;
                    if (preservedSliderConfig.activeIndex < swiper.slides.length) {
                      swiper.slideTo(preservedSliderConfig.activeIndex, 0, false);
                    }
                  }
                }, 50);
              }, 100);
            } else if (newHorizontalSlider) {
              // If no preserved state, just ensure proper initialization.
              if (/** @type {any} */ (newHorizontalSlider).swiper) {
                /** @type {any} */ (newHorizontalSlider).swiper.update();
              } else {
                // Fallback: ensure slider is initialized if it wasn't automatically.
                setTimeout(() => {
                  if (!(/** @type {any} */ (newHorizontalSlider).swiper)) {
                    /** @type {any} */ (newHorizontalSlider).initialize();
                  }
                }, 200);
              }
            }
          }, 100);
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // Update main cart items
      const cartUrl = /** @type {any} */ (window).routes?.cart_url || '/cart';
      fetch(`${cartUrl}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
          }

          // Re-initialise lightbox after DOM updates.
          this.initCartPageLightbox();
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  /**
   * Initialise lightbox behaviour for the main cart page.
   */
  initCartPageLightbox() {
    this.pageLightbox = document.querySelector('.js-cart-page-lightbox');
    if (!this.pageLightbox) return;

    const closeButton = this.pageLightbox.querySelector('.js-cart-page-lightbox-close');
    if (
      closeButton &&
      closeButton instanceof HTMLElement &&
      !(/** @type {any} */ (closeButton).__cartPageLightboxBound)
    ) {
      closeButton.addEventListener('click', () => this.closeCartPageLightbox());
      /** @type {any} */ (closeButton).__cartPageLightboxBound = true;
    }

    if (!this.__cartPageDelegated) {
      document.addEventListener('click', (event) => {
        const trigger =
          event.target instanceof Element
            ? event.target.closest('.js-cart-lightbox-trigger, .js-cart-page-lightbox-trigger')
            : null;
        if (trigger && trigger instanceof HTMLElement && document.getElementById('main-cart-items')) {
          event.preventDefault();
          event.stopPropagation();
          const imageSrc = trigger.getAttribute('data-image-src') || trigger.getAttribute('src');
          const imageAlt = trigger.getAttribute('data-image-alt') || trigger.getAttribute('alt') || 'Cart image';
          if (imageSrc) this.openCartPageLightbox(imageSrc, imageAlt);
        }
      });
      this.__cartPageDelegated = true;
    }

    if (!this.__cartPageOverlayBound) {
      this.pageLightbox.addEventListener('click', (event) => {
        if (event.target === this.pageLightbox) {
          this.closeCartPageLightbox();
        }
      });
      this.__cartPageOverlayBound = true;
    }

    if (!this.__cartPageEscBound) {
      document.addEventListener('keydown', (event) => {
        if (
          event.key === 'Escape' &&
          this.pageLightbox &&
          this.pageLightbox instanceof HTMLElement &&
          this.pageLightbox.style.display === 'flex'
        ) {
          this.closeCartPageLightbox();
        }
      });
      this.__cartPageEscBound = true;
    }
  }

  openCartPageLightbox(imageSrc, imageAlt) {
    if (!this.pageLightbox || !(this.pageLightbox instanceof HTMLElement)) return;
    if (this.pageLightbox.style.display === 'flex') return;
    const lightboxImage = this.pageLightbox.querySelector('.js-cart-page-lightbox-image');
    if (lightboxImage && lightboxImage instanceof HTMLImageElement) {
      lightboxImage.src = imageSrc;
      lightboxImage.alt = imageAlt || 'Cart image';
    }
    this.pageLightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  closeCartPageLightbox() {
    if (!this.pageLightbox || !(this.pageLightbox instanceof HTMLElement)) return;
    this.pageLightbox.style.display = 'none';
    document.body.style.overflow = '';
  }

  /**
   * Gets the sections that need to be rendered during cart updates.
   * @returns {Array} Array of section objects with id, section, and selector properties.
   */
  getSectionsToRender() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    return [
      {
        id: 'main-cart-items',
        section: mainCartItems?.dataset?.id || '',
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: mainCartFooter?.dataset?.id || '',
        selector: '.cart__footer'
      }
    ];
  }

  /**
   * Updates the quantity of a cart item.
   * @param {string} line - The line item index.
   * @param {number} quantity - The new quantity.
   * @param {string} name - The input name attribute.
   * @param {string} variantId - The variant ID.
   */
  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    // Clear any existing error messages early.
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    const cartChangeUrl = /** @type {any} */ (window).routes?.cart_change_url || '/cart/change.js';
    fetch(cartChangeUrl, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok.');
        }
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement && quantityElement instanceof HTMLInputElement) {
            quantityElement.value = quantityElement.getAttribute('value') || '';
          }
          this.updateLiveRegions(line, parsedState.errors);
          if (errors) errors.textContent = parsedState.errors;
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (sectionElement) {
            const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
            if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          }
        });

        updateCartCountBubble(parsedState.item_count || 0);

        const updatedValue = parsedState.items[parseInt(line, 10) - 1]
          ? parsedState.items[parseInt(line, 10) - 1].quantity
          : undefined;
        let message = '';
        if (
          items.length === parsedState.items.length &&
          quantityElement instanceof HTMLInputElement &&
          updatedValue !== parseInt(quantityElement.value, 10)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = /** @type {any} */ (window).cartStrings?.error || 'Error updating cart';
          } else {
            message =
              /** @type {any} */ (window).cartStrings?.quantityError?.replace('[quantity]', updatedValue) ||
              `Quantity updated to ${updatedValue}`;
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          const nameElement = lineItem.querySelector(`[name="${name}"]`);
          if (cartDrawerWrapper && nameElement instanceof HTMLElement) {
            trapFocus(cartDrawerWrapper, nameElement);
          } else if (nameElement instanceof HTMLElement) {
            nameElement.focus();
          }
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          const emptyElement = cartDrawerWrapper.querySelector('.drawer__inner-empty');
          const linkElement = cartDrawerWrapper.querySelector('a');
          if (emptyElement && linkElement) {
            trapFocus(emptyElement, linkElement);
          }
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          const cartItemName = document.querySelector('.cart-item__name');
          if (cartItemName) {
            trapFocus(cartDrawerWrapper, cartItemName);
          }
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', variantId: variantId });
      })
      .catch((error) => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        // Only show error for actual network failures.
        if (error.message === 'Network response was not ok.') {
          const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          if (errors) {
            errors.textContent = /** @type {any} */ (window).cartStrings?.error || 'Error updating cart';
          }
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * Updates live regions for accessibility announcements.
   * @param {string} line - The line item index.
   * @param {string} message - The message to announce.
   */
  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = message;
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  /**
   * Gets the inner HTML of a section from the parsed HTML.
   * @param {string} html - The HTML string to parse.
   * @param {string} selector - The CSS selector to find the element.
   * @returns {string} The inner HTML of the selected element.
   */
  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  /**
   * Enables loading state for a specific line item.
   * @param {string} line - The line item index.
   */
  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Disables loading state for a specific line item.
   * @param {string} line - The line item index.
   */
  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            if (event.target instanceof HTMLInputElement) {
              const body = JSON.stringify({ note: event.target.value });
              const cartUpdateUrl = /** @type {any} */ (window).routes?.cart_update_url || '/cart/update.js';
              fetch(cartUpdateUrl, { ...fetchConfig(), ...{ body } });
            }
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
